
'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Project } from '@/domains/projects/schemas';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, Trash2, Loader2, FolderPlus, ArrowRightLeft } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ProjectCardProps {
    project: Partial<Project & { isSeries?: boolean }>;
    onDelete: (project: Partial<Project>) => void;
    onDuplicate: (project: Partial<Project>) => void;
    onTransfer?: (project: Partial<Project>) => void;
    /** Optional: available groups for "Add to group" action */
    availableGroups?: { id: string; name: string }[];
    onAddToGroup?: (projectId: string, groupId: string) => void;
}

/**
 * Checks if series context is stale (older than 7 days).
 */
function isSeriesContextStale(lastUpdated: string | undefined, thresholdDays: number = 7): boolean {
    if (!lastUpdated) {
        return true;
    }

    const lastUpdatedDate = new Date(lastUpdated);
    const now = new Date();
    const daysSinceUpdate = (now.getTime() - lastUpdatedDate.getTime()) / (1000 * 60 * 60 * 24);

    return daysSinceUpdate > thresholdDays;
}

export function ProjectCard({ project, onDelete, onDuplicate, onTransfer, availableGroups, onAddToGroup }: ProjectCardProps) {
    const [isDeleting, setIsDeleting] = useState(false);

    // Check if series context is active and its freshness
    const hasSeriesContext = !!project.seriesContext?.content;
    const isStale = hasSeriesContext && isSeriesContextStale(project.seriesContext?.lastUpdated);

    const handleDuplicateClick = (e: React.MouseEvent) => {
        e.preventDefault();
        onDuplicate(project);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.preventDefault();
        onDelete(project);
    };

    const handleTransferClick = (e: React.MouseEvent) => {
        e.preventDefault();
        onTransfer?.(project);
    };

    const isSeries = !!(project as any).isSeries;

    return (
        <Card className={cn(
            "hover:border-primary transition-colors duration-300 group flex flex-col relative",
            isDeleting && 'opacity-50 pointer-events-none'
        )}>
            {isDeleting && (
                <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10 rounded-lg">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            )}
            <Link href={`/projects/${project.id!}`} className="flex flex-col flex-grow">
                <CardHeader>
                    <div className="flex justify-between items-start">
                        <div className="flex-grow">
                            <div className="flex items-center gap-2 flex-wrap">
                                <CardTitle className="text-lg group-hover:text-primary transition-colors duration-300">
                                    {project.title}
                                </CardTitle>
                                {hasSeriesContext && (
                                    <Badge
                                        className={cn(
                                            "text-xs",
                                            isStale
                                                ? "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-500/30"
                                                : "bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30"
                                        )}
                                        variant="outline"
                                    >
                                        📚 Series Context Active
                                    </Badge>
                                )}
                            </div>
                            <CardDescription>
                                Last updated {project.updatedAt ? formatDistanceToNow(new Date(project.updatedAt), { addSuffix: true }) : 'recently'}
                            </CardDescription>
                        </div>
                        <div className="flex items-center">
                            {availableGroups && availableGroups.length > 0 && onAddToGroup && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={(e) => e.preventDefault()}
                                    aria-label={`Add ${project.title} to a group`}
                                  >
                                    <FolderPlus className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {availableGroups.map((g) => (
                                    <DropdownMenuItem
                                      key={g.id}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        onAddToGroup(project.id!, g.id);
                                      }}
                                    >
                                      {g.name}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={handleDuplicateClick}
                              aria-label={`Duplicate project ${project.title}`}
                            >
                                <Copy className="h-4 w-4" />
                            </Button>
                            {onTransfer && (
                              <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={handleTransferClick}
                                        disabled={isSeries}
                                        aria-label={`Transfer project ${project.title}`}
                                      >
                                        <ArrowRightLeft className="h-4 w-4" />
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  {isSeries && (
                                    <TooltipContent>
                                      <p>Unlink this project from its series before transferring</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                                onClick={handleDeleteClick}
                                aria-label={`Delete project ${project.title}`}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="flex-grow">
                    <p className="text-sm text-muted-foreground line-clamp-2">
                        {project.blurb}
                    </p>
                </CardContent>
            </Link>
            <CardFooter>
                <p className="text-xs text-muted-foreground/50 ml-auto font-mono">
                    ID: {project.id}
                </p>
            </CardFooter>
        </Card>
    );
}
